/**
 * Single source of truth for company, contact, address, social, and external service URLs.
 * Consumed by `marketing/` and `portal/` workspaces. Keep framework-agnostic (no React/Next imports).
 */

// ---------------------------------------------------------------------------
// Leadership + organisation (Slice 1 of
// _bmad-output/COMPANY_PROFILE_ORGANOGRAM_ABOUT_US_PLAN_2026-10-01.md).
//
// ONE data source for the About page, the organogram component and the
// company-profile PDF. Two hand-maintained copies would drift exactly the way
// the three letterhead copies already have (plan §3.4). Change of people =
// edit this block + rebuild the PDF the same day.
//
// MODEL — people and positions are separate on purpose. A person can hold
// more than one seat: Phologo Machailoe is the board's Executive Director AND
// the CEO who runs operations. Giving him one entry
// with one `band` cannot express that; giving him three person entries would
// copy his name, bio and photo three times and let them disagree. So:
//   - `leadership`   = who someone is (name, bio, photo, consent-gated
//                      publish date) — one row per human or entity;
//   - `organisation` = the seats (band, title, reporting line) — one row per
//                      position, pointing at a person by `personId`.
// `reportsTo` points at a POSITION id, not a person id, because a reporting
// line runs to a role (Dineo reports to the CEO, not to Phologo-the-
// director).
//
// NO SHAREHOLDINGS on any public surface (owner, 2026-10-10): the site says
// only that SightHost is part of the Hindsight Consulting Group. The top band
// is the group, not 'Shareholders', and carries no percentages.
// ---------------------------------------------------------------------------

/** The three organogram bands (plan §4). Kept separate so "Chairman" stays honest:
 * Sipho chairs the GROUP, not the SightHost board. */
export type OrgBand = 'group' | 'board' | 'operations';

/**
 * Team-card grouping inside the operations band (owner 2026-10-10: the sales
 * consultants are not management). The organogram keeps one 'Operations' level
 * so the reporting lines to the CEO stay drawn; only the cards split.
 */
export type TeamGroup = 'management' | 'sales';

export interface LeadershipPerson {
  id: string;
  name: string;
  /** 'organisation' for a corporate shareholder (Hindsight Consulting). */
  kind: 'person' | 'organisation';
  /**
   * 2–3 line public bio. Deliberately UNDEFINED until input D5 (written POPIA
   * consent + supplied copy) arrives — never invent one.
   */
  bio?: string;
  /** Headshot path. Undefined until D5; renderers fall back to initials. */
  photo?: string;
  /** Public LinkedIn URL. Undefined until D5. */
  linkedin?: string;
  /**
   * ISO date (YYYY-MM-DD, SAST) from which this person may appear on any
   * public surface. `null` = hidden everywhere. This is how Dr Seele goes live
   * on her CIPC filing date without a code review of every renderer (D2): a
   * tender profile must match the CIPC/CSD record on the day it is sent.
   */
  publishFrom: string | null;
}

export interface OrgPosition {
  id: string;
  personId: string;
  band: OrgBand;
  /** Public title, exactly as it may be printed. */
  title: string;
  /** Position id this seat reports to (solid line). */
  reportsTo?: string;
  /** True for a seat on the SightHost board. Every board-band seat must be one. */
  isDirector?: boolean;
  /** Operations seats only: which team-card group the seat appears under. */
  teamGroup?: TeamGroup;
}

const LEADERSHIP: ReadonlyArray<LeadershipPerson> = [
  {
    // D1 (a), 2026-10-01: Sipho chairs the Hindsight Consulting Group, not
    // SightHost. He resigned as a SightHost director on 2026-09-15; a
    // chairman must be a director, so "Chairman of SightHost" and "Executive
    // Chairman" are banned (gate test + HR doc gate).
    id: 'sipho-mampe',
    name: 'Sipho Mampe',
    kind: 'person',
    publishFrom: '2026-10-01',
  },
  {
    id: 'phologo-machailoe',
    name: 'Phologo Machailoe',
    kind: 'person',
    publishFrom: '2026-10-01',
  },
  {
    // D2, 2026-10-01: appointment NOT yet filed at CIPC. Stays null until the
    // filing date is known; set it to that date and every surface picks her up.
    id: 'palesa-seele',
    name: 'Dr Palesa Seele',
    kind: 'person',
    publishFrom: null,
  },
  {
    // D3, 2026-10-01: SightHost employee, reports to the CEO. The independent
    // clause 10.3 reconciliation stays with the Shareholder-appointed
    // Bookkeepers (Hindsight Online), who are outside the public chart.
    id: 'dineo-rametse',
    name: 'Dineo Rametsi',
    kind: 'person',
    publishFrom: '2026-10-01',
  },
  {
    id: 'orapeleng-banda',
    name: 'Orapeleng Banda',
    kind: 'person',
    publishFrom: '2026-10-01',
  },
  {
    id: 'loyiso-tuswa',
    name: 'Loyiso Tuswa',
    kind: 'person',
    publishFrom: '2026-10-01',
  },
];

const ORGANISATION: { bands: ReadonlyArray<{ id: OrgBand; label: string }>; positions: ReadonlyArray<OrgPosition> } = {
  bands: [
    { id: 'group', label: 'Hindsight Consulting Group' },
    { id: 'board', label: 'Board of Directors' },
    { id: 'operations', label: 'Operations' },
  ],
  positions: [
    // --- Group -----------------------------------------------------------
    // D1: no reporting line from the Chairman into the board or management.
    {
      id: 'chairman-hindsight',
      personId: 'sipho-mampe',
      band: 'group',
      title: 'Chairman, Hindsight Consulting Group',
    },
    // --- Board -----------------------------------------------------------
    // Currently the SOLE director on the CIPC record.
    {
      id: 'director-phologo',
      personId: 'phologo-machailoe',
      band: 'board',
      title: 'Chief Executive Officer & Executive Director',
      isDirector: true,
    },
    {
      id: 'director-seele',
      personId: 'palesa-seele',
      band: 'board',
      title: 'Independent Non-Executive Director',
      isDirector: true,
    },
    // --- Management ------------------------------------------------------
    { id: 'ceo', personId: 'phologo-machailoe', band: 'operations', title: 'Chief Executive Officer & Lead Designer', teamGroup: 'management' },
    { id: 'financial-manager', personId: 'dineo-rametse', band: 'operations', title: 'Financial Manager', reportsTo: 'ceo', teamGroup: 'management' },
    // D4, 2026-10-01: "Sales Consultant" is the PUBLIC title. The contracts
    // keep "Sales Agent" and the portal role stays `sales_agent` — a public
    // job title does not amend an employment contract.
    { id: 'sales-banda', personId: 'orapeleng-banda', band: 'operations', title: 'Sales Consultant', reportsTo: 'ceo', teamGroup: 'sales' },
    { id: 'sales-tuswa', personId: 'loyiso-tuswa', band: 'operations', title: 'Sales Consultant', reportsTo: 'ceo', teamGroup: 'sales' },
  ],
};

/**
 * People who may appear publicly on `onDate` (ISO YYYY-MM-DD, SAST — pass
 * `sastDateKey()` from the portal or the build date from the PDF builder).
 * `publishFrom: null` is never visible. Pure: no clock read here, so the
 * caller decides what "today" means and tests can pin it.
 */
export function visibleLeadership(
  onDate: string,
  people: ReadonlyArray<LeadershipPerson> = LEADERSHIP,
): LeadershipPerson[] {
  return people.filter((p) => p.publishFrom !== null && p.publishFrom <= onDate);
}

/** Positions whose holder is visible on `onDate`. Renderers draw only these. */
export function visiblePositions(
  onDate: string,
  people: ReadonlyArray<LeadershipPerson> = LEADERSHIP,
  positions: ReadonlyArray<OrgPosition> = ORGANISATION.positions,
): OrgPosition[] {
  const visible = new Set(visibleLeadership(onDate, people).map((p) => p.id));
  return positions.filter((pos) => visible.has(pos.personId));
}

export const siteConfig = {
  // Identity
  name: 'SightHost',
  legalName: 'SightHost (Pty) Ltd',
  registration: '2018/238217/07',
  vatNumber: '',
  foundingDate: '2018',

  /**
   * Corporate parent. SightHost (Pty) Ltd is a subsidiary of Hindsight
   * Consulting (Pty) Ltd — a South African web development business. Surface
   * this on the About page so the group relationship is transparent to
   * customers.
   *
   * NOT "wholly-owned" (owner instruction 2026-09-01): SightHost ceases to be
   * wholly owned on 2026-09-15, when 10% issues to its incoming CEO. Under
   * Companies Act s3(1)(a) a subsidiary turns on control of a majority of the
   * voting rights, so the word stays accurate at 90%. `relationship` exists so
   * that the phrase has ONE definition — AboutPage.tsx previously hardcoded
   * "wholly-owned subsidiary" in JSX while reading every other fact from here,
   * which is how the claim survived in a place nobody thought to look.
   */
  parent: {
    name: 'Hindsight Consulting (Pty) Ltd',
    /**
     * The ONLY public description of the relationship (owner, 2026-10-10):
     * "part of the Hindsight Consulting Group". No shareholding, percentage,
     * "subsidiary" or "parent company" wording on the website or portal.
     */
    shortName: 'Hindsight Consulting Group',
    /** Reads as "<legalName> is <relationship> <shortName>". */
    relationship: 'part of the',
    /** Hindsight Consulting's own web presence — currently the hindsight.africa surface. */
    url: 'https://hindsight.africa',
    description:
      'Hindsight Consulting is a South African web development business. Hindsight builds the software; SightHost runs the infrastructure it sits on.',
  },

  /**
   * Group + industry affiliations rendered on the About page. Add new entries
   * here rather than hard-coding in AboutPage.tsx.
   *
   * Sight Business Solutions is the bookkeeping + HR platform hosted at
   * hindsight.africa. SightHost hosts the infrastructure under the group;
   * the relationship is a real cross-group case study, not a paid
   * partnership.
   */
  affiliations: [
    {
      name: 'Sight Business Solutions',
      url: 'https://hindsight.africa',
      relationship: 'sister platform',
      description:
        'A bookkeeping + HR platform for South African SMEs, hosted at hindsight.africa. SightHost provides the underlying hosting infrastructure.',
    },
  ] as ReadonlyArray<{
    name: string;
    url: string;
    relationship: string;
    description: string;
  }>,

  /**
   * People + organogram seats. See the model note above LEADERSHIP. Render
   * through `visibleLeadership()` / `visiblePositions()`, never by reading
   * these arrays directly — the raw data includes people not yet publishable.
   */
  leadership: LEADERSHIP,
  organisation: ORGANISATION,

  /**
   * Downloadable company profile (D12). `pdfPath` is the site-relative path of
   * the published PDF, e.g. '/downloads/SIGHTHOST_COMPANY_PROFILE_v1.0.pdf'.
   * Deliberately `null` until Slice 4 builds and publishes it: the About page
   * renders its "Download company profile" button ONLY when this is set — a
   * button to a 404 is worse than no button.
   */
  companyProfile: {
    pdfPath: null as string | null,
  },

  /**
   * VAT registration status. Drives invoice header text ("INVOICE" vs "TAX INVOICE"),
   * default VAT rate on new line items, and whether SightHost's VAT number renders
   * on documents. See memory/vat_model.md for the full design.
   *
   * Switching to true requires:
   *   1. Set `vatRegistered: true`
   *   2. Set `vatNumber` above to the SARS-issued number
   *   3. Update `service_plan.vat_rate` rows to 0.15 via a data migration
   * No schema migration is required — the columns already exist with vat_rate=0.
   */
  vatRegistered: false as boolean,
  /** Default VAT rate applied to NEW invoice lines when `vatRegistered` is true. */
  defaultVatRate: 0.15,

  // Web — flipped to sighthost.online on 2026-05-25 per Phase D rebrand cutover.
  url: 'https://www.sighthost.online',
  domain: 'sighthost.online',

  // Description / SEO
  // F-1 (2026-10-01): "& fiber internet" removed. SightHost is not a fibre
  // provider (Approved Claims §2.3, §6.3.1) and this string is the site meta
  // description search engines show under every result.
  // 2026-10-15: said "from R129/mo" while the R79 Entry plan was on sale, so the
  // one line searchers read undersold our own entry price. Kept under 155 chars.
  description:
    'Web hosting from R79/mo, .co.za domains at R99/yr and websites built in 48 working hours. South African company, local support, POPIA compliant.',
  tagline: 'Hosting South Africa',
  keywords: [
    'web hosting south africa',
    'south african web hosting',
    'hosting south africa',
    'sa web hosting',
    'johannesburg web hosting',
    'gauteng hosting provider',
    'domain registration south africa',
    'register .co.za domain',
    // Fibre keywords disabled 2026-05-08 — service is not yet active.
    // Restoring these now would mislead search engines + visitors. Re-enable
    // when the fibre service launches.
    // 'fiber internet south africa',
    // 'fibre installation johannesburg',
    'cpanel hosting',
    'email hosting south africa',
    'ssl certificates',
    'wordpress hosting south africa',
    'affordable web hosting',
    'reliable hosting',
    '99.9% uptime hosting',
    'ssd hosting south africa',
    'popia compliant hosting',
    'small business hosting',
    'professional hosting services',
  ],

  // Contact — flipped to sighthost.online on 2026-05-25 per Phase D rebrand cutover.
  // cPanel forwarders for support/info/billing/legal/privacy@sighthost.online route to
  // the existing inbox so replies still land in the same place during the overlap window.
  phone: '+27720261267',
  phoneDisplay: '072 026 1267',
  email: 'info@sighthost.online',
  supportEmail: 'support@sighthost.online',
  billingEmail: 'billing@sighthost.online',
  legalEmail: 'legal@sighthost.online',
  privacyEmail: 'privacy@sighthost.online',
  informationOfficer: {
    name: 'Information Officer',
    email: 'privacy@sighthost.online',
  },
  // Truthful-claims (2026-08-11): this said '24/7' while the business runs a
  // business-hour desk. Two things ARE genuinely around the clock — the portal
  // accepts tickets at any hour, and platform monitoring never sleeps — so say
  // those, and say them separately from staffed human support.
  // Confirmed by Mr Mampe 2026-08-11: 08:30-16:30, weekdays. This is the ONE
  // place these hours are written — /contact, /sla and the welcome email all
  // read from here rather than restating them.
  supportHours: 'Mon-Fri 08:30-16:30 (SAST)',
  supportHoursShort: 'Business hours (SAST)',
  // The same hours in machine form, for structured data (JsonLd.tsx). Change
  // both together. Before 2026-10-15 the schema carried its own literals
  // (Mon-Fri 08:00-18:00 + Saturday, and a 24/7 tech-support line) that
  // matched nothing the business does.
  supportHoursSpec: {
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '08:30',
    closes: '16:30',
  },
  // Response promise, quoted from the SLA table (/sla section 4): High = 4
  // business hours, Low (general questions) = 2 business days. /contact said
  // "within 4 hours" and /support said "within 24 hours" for the same mailbox;
  // both now read this line. If the SLA table changes, change this.
  supportResponse: 'Urgent issues within 4 business hours, general questions within 2 business days',

  // Address. Office moved 2026-11-01 (was Northgate Office Park, Northriding,
  // Randburg, 2162). Coordinates are the owner's Google Maps pin for Block F.
  // This is the ONE copy the website reads; the other three are the email
  // footer (supabase/functions/_shared/email/EmailLayout.tsx, Deno cannot
  // import this package), system_setting.domains_default_registrant_address,
  // and documents already issued (which keep the address they were sent with).
  address: {
    street: 'Block F, Saint Andrews Office Park, 0B Meadowbrook Lane',
    locality: 'Epsom Downs',
    city: 'Bryanston',
    region: 'Gauteng',
    postalCode: '2192',
    country: 'ZA',
    countryName: 'South Africa',
    coordinates: { lat: -26.040611, lng: 28.013413 },
  },

  // Social — only profiles that actually exist; add more here when opened
  social: {
    facebook: 'https://www.facebook.com/sighthost.online',
    linkedin: 'https://www.linkedin.com/company/sighthost/',
  },

  // Operations
  businessType: 'InternetServiceProvider',
  numberOfEmployees: { min: 2, max: 10 },
  paymentAccepted: ['Credit Card', 'Debit Card', 'EFT', 'PayFast'],
  currenciesAccepted: 'ZAR',
  // 2026-10-15: was 'R129 - R499', omitting the R79 Entry plan and the
  // R3,990 Studio website. Cheapest hosting plan to Studio Starter.
  priceRange: 'R79 - R3,990',

  /**
   * SightHost customer portal SPA on Cloudflare Pages.
   * - `productionUrl` is where the portal SPA is deployed.
   * - `devUrl` is the local Vite dev server for `pnpm dev:portal`.
   * - All marketing CTAs resolve through `marketing/src/config/portal.ts → usePortalUrl()` / `buildPortalUrl()`.
   * - Flipped to portal.sighthost.online on 2026-05-25 (Phase D cutover). The old
   *   portal.afrilinktelecon.co.za domain stays as a Cloudflare 301 redirect target
   *   per CLAUDE.md operational notes; in-flight email links keep working.
   */
  portal: {
    productionUrl: 'https://portal.sighthost.online',
    devUrl: 'http://localhost:5174',
    signInPath: '/sign-in',
    signUpPath: '/sign-up',
    dashboardPath: '/app',
    hostingPath: '/app/services',
    hostingOrderPath: '/app/services/new',
    domainsPath: '/app/domains',
    // Must match the portal route in portal/src/apps/client/AppRouter.tsx
    // ('domains/register'). Was '/app/domains/new' until 2026-07-02 — a dead
    // route; every marketing Domains-page CTA landed on the portal 404.
    domainRegisterPath: '/app/domains/register',
    domainTransferPath: '/app/domains/transfer',
    supportPath: '/app/support',
    submitTicketPath: '/app/support/new',
    knowledgeBasePath: '/app/knowledgebase',
    contactHandlerPath: '/api/marketing-contact',
  },

  /**
   * Public Supabase Edge Function that receives marketing contact-form
   * submissions and delivers them to support@afrilinktelecon.co.za via the
   * notification-runner. Replaces the legacy
   * billing.afrilinktelecon.co.za/contact-handler.php endpoint as of
   * 2026-05-04.
   */
  marketingContactEndpoint:
    'https://djnwjeslpomvornubmsv.supabase.co/functions/v1/marketing-contact',

  /**
   * Public Edge Function that captures client-side JS errors from the
   * marketing site (M3.13). Marketing main.tsx wires window.onerror +
   * window.onunhandledrejection to POST here; rows land in marketing_error
   * for staff review via Supabase dashboard or admin reports later.
   */
  marketingErrorLogEndpoint:
    'https://djnwjeslpomvornubmsv.supabase.co/functions/v1/marketing-error-log',

  /**
   * Public Edge Function for anonymous domain-availability checks (no JWT —
   * verify_jwt=false since 2026-07-01; per-IP rate-limited). Drives the
   * site-wide DomainSearch widget on the marketing site and the portal
   * order-funnel Step 2 badge. Real-time ZACR/registry answer — never cached.
   */
  domainAvailabilityEndpoint:
    'https://djnwjeslpomvornubmsv.supabase.co/functions/v1/domain-availability',

  /**
   * TLDs the DomainSearch widget offers, with retail pricing where self-serve
   * registration is live. Verified against the domains.co.za reseller API on
   * 2026-07-02 (all entries below returned real registry answers through the
   * production service identity). `selfServe: true` = pricing row exists in
   * `system_setting.domains_pricing` (migration `20260702083000` — owner set
   * the published marketing prices live), so the portal register funnel
   * completes; the unpriced tail is concierge-only (contact/ticket).
   */
  domainSearch: {
    /** Appended when the visitor types a bare name with no dot. */
    defaultTld: 'co.za',
    tlds: [
      // Corrected 2026-09-13 against live wholesale. Six of these were sold
      // BELOW COST — .capetown/.joburg/.durban at HALF cost (R149 vs R299).
      // Rule: .za family = max(R10, 10% of cost); everything else = 15% of cost,
      // rounded up to the next R_9. priceZar is the REGISTRATION price.
      // .africa is HELD above the rule at R299: its cost moved R99 -> R189 in
      // eight weeks during 2025, and the margin is the shock absorber.
      // Must stay in step with system_setting.domains_pricing (the authority)
      // and with marketing DomainsPage. See
      // _bmad-output/DOMAIN_WHOLESALE_PRICING_2026-09-13.md
      { tld: 'co.za',    priceZar: 99,   selfServe: true },  // cost 89.00
      { tld: 'org.za',   priceZar: 99,   selfServe: true },  // cost 89.00
      { tld: 'net.za',   priceZar: 99,   selfServe: true },  // cost 89.00
      { tld: 'web.za',   priceZar: 99,   selfServe: true },  // cost 89.00
      { tld: 'com',      priceZar: 269,  selfServe: true },  // cost 229.88 -> 17.0%
      { tld: 'org',      priceZar: 289,  selfServe: true },  // cost 246.30 -> 17.3%
      { tld: 'net',      priceZar: 289,  selfServe: true },  // cost 246.30 -> 17.3%
      { tld: 'africa',   priceZar: 299,  selfServe: true },  // cost 189.00 -> 58.2%; renew cost 259.00 -> 15.4%
      { tld: 'capetown', priceZar: 349,  selfServe: true },  // cost 299.00 -> 16.7%
      { tld: 'joburg',   priceZar: 349,  selfServe: true },  // cost 299.00 -> 16.7%
      { tld: 'durban',   priceZar: 349,  selfServe: true },  // cost 299.00 -> 16.7%
      { tld: 'online',   priceZar: null, selfServe: false },
      { tld: 'store',    priceZar: null, selfServe: false },
      { tld: 'xyz',      priceZar: null, selfServe: false },
      { tld: 'io',       priceZar: null, selfServe: false },
    ] as ReadonlyArray<{ tld: string; priceZar: number | null; selfServe: boolean }>,
    /** Shown as one-tap chips under the hero search input. */
    featuredTlds: ['co.za', 'com', 'africa', 'capetown', 'online'],
  },

  /**
   * Cloudflare Turnstile — captcha for marketing forms (M2.10). Site key is
   * public-by-design (rendered in the browser); the matching SECRET key is
   * stored as TURNSTILE_SECRET_KEY in the marketing-contact Supabase Edge
   * Function env vars (NOT here, NOT committed). Provisioned 2026-05-07.
   */
  turnstile: {
    siteKey: '0x4AAAAAADK-U90WVxnFPQla',
    verifyUrl: 'https://challenges.cloudflare.com/turnstile/v0/siteverify',
  },

  /**
   * Google Ads conversion tracking (client-side, Consent Mode v2). Both values
   * are PUBLIC by design. Empty strings keep tracking FULLY INERT — no gtag.js
   * loads and no conversion fires — so the feature ships ahead of setup. The
   * owner pastes these in after creating the "Purchase" conversion action in
   * Google Ads (Conversion ID → conversionId; the label AFTER the slash in
   * send_to → purchaseLabel).
   * See _bmad-output/CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md.
   */
  googleAds: {
    /** Google tag / Conversion ID. Set 2026-07-09 — loads the tag site-wide. */
    conversionId: 'AW-18309451991' as string,
    /**
     * "Purchase" conversion label (part after the slash in send_to). From the
     * NATIVE manual Google Ads Purchase conversion action (Event → destination
     * AW-18309451991), set 2026-07-09. fireAdsPurchase now sends
     * 'AW-18309451991/5FJ4CLHgl80cENeh0JpE' on a confirmed-paid invoice.
     */
    purchaseLabel: '5FJ4CLHgl80cENeh0JpE' as string,
  },

  // Brand
  brand: {
    primary: '#F58C1E',
    secondary: '#276E84',
    accent: '#FFB347',
    // The browser bar is teal, as the brand pack specifies (adopted 2026-11-05).
    // The two PWA manifests and both index.html files carry the same value, and
    // check-tokens.mjs compares all of them.
    themeColor: '#276E84',
  },

  /**
   * Banking details. Rendered on every invoice (mirrors
   * hindsight-accounting-suite ProfessionalTemplate layout) and, since
   * 2026-08-12, on six marketing pages via BankDetailsPanel and in the agent
   * bank-confirmation letter.
   *
   * **This object has a second copy: `BANK` in
   * supabase/functions/_shared/brand.ts.** Edge Functions run on Deno and cannot
   * import this workspace, so the duplication is deliberate — change one, change
   * the other. (Before 2026-08-12 the email block did not even have that copy;
   * the account number was an inline JSX literal.)
   *
   * `accountName` must match the printed name on the Capitec bank statement
   * screen — if they drift, customer EFTs may be held for review. See
   * memory/sighthost_rebrand_in_progress.md.
   */
  bank: {
    name: 'Capitec Business Account',
    accountName: 'SightHost (Pty) Ltd',
    accountNumber: '1054752672',
    branchCode: '450105',
    accountType: 'Business',
    swiftCode: '',
    referenceHint: 'Use invoice number as reference',
  },
} as const;

export type SiteConfig = typeof siteConfig;
